import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import MobileNavDrawer from "../components/MobileNavDrawer";
import ImageCropper from "../components/ImageCropper";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import debounce from "lodash/debounce";
import { DeviceContext } from "../context/DeviceContext";
import { uploadFile } from "../utils/uploadFile";

const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DL.bgLight,
    borderRadius: "8px",
    "& fieldset": { borderColor: DL.border },
    "&:hover fieldset": { borderColor: DL.borderSubtle },
    "&.Mui-focused fieldset": { borderColor: DL.green },
  },
  "& .MuiInputBase-input": { color: DL.textPrimary, fontSize: "0.9rem" },
  "& .MuiInputLabel-root": { color: DL.textMuted, fontSize: "0.9rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
  "& .MuiFormHelperText-root": { color: DL.textMuted },
};

const primaryBtnSx = {
  background: "linear-gradient(to right, #10b981, #059669)",
  color: "#fff",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.95rem",
  py: 1.25,
  "&:hover": { filter: "brightness(110%)" },
  "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
};

const secondaryBtnSx = {
  backgroundColor: DL.bgLight,
  border: `1px solid ${DL.textMuted}`,
  color: DL.textPrimary,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  py: 1.25,
  px: 3,
  "&:hover": { borderColor: DL.textSecondary, backgroundColor: "rgba(255,255,255,0.06)" },
  "&:disabled": { opacity: 0.3 },
};

const steps = ["Details", "Icon", "Banner", "Review"];

const CommunityNameInput = ({ setCommunityName }) => {
  const [query, setQuery] = useState("");
  const [isAvailable, setIsAvailable] = useState(null);

  const checkAvailability = debounce(async (name) => {
    if (name.length > 0) {
      const response = await axios.get(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/community/search?query=${name}`
      );
      const data = response.data;
      setIsAvailable(!data.some((item) => item.name === name));
    } else {
      setIsAvailable(null);
    }
  }, 3000);

  useEffect(() => {
    checkAvailability(query);
  }, [query]);

  return (
    <Box>
      <TextField
        label="Community Name"
        variant="outlined"
        fullWidth
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setCommunityName(e.target.value);
        }}
        sx={inputSx}
      />
      {isAvailable !== null && (
        <Alert
          severity={isAvailable ? "success" : "error"}
          sx={{
            mt: 1,
            backgroundColor: DL.bgLight,
            color: isAvailable ? DL.green : DL.red,
            border: `1px solid ${isAvailable ? DL.borderGreen : "rgba(255,100,103,0.3)"}`,
            borderRadius: "8px",
            "& .MuiAlert-icon": { color: isAvailable ? DL.green : DL.red },
          }}>
          {isAvailable ? "Community name is available" : "Community name is already taken"}
        </Alert>
      )}
    </Box>
  );
};

function CreateCommunity() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Details
  const [communityName, setCommunityName] = useState("");
  const [communityDescription, setCommunityDescription] = useState("");
  const [communityRules, setCommunityRules] = useState("");

  // Icon
  const [iconUrl, setIconUrl] = useState(null);
  const [iconInputName, setIconInputName] = useState("");
  const [croppedIcon, setCroppedIcon] = useState(null);
  const [iconCrop, setIconCrop] = useState({ x: 0, y: 0 });
  const [iconZoom, setIconZoom] = useState(1);
  const [iconChanged, setIconChanged] = useState(false);

  // Banner
  const [bannerUrl, setBannerUrl] = useState(null);
  const [bannerInputName, setBannerInputName] = useState("");
  const [croppedBanner, setCroppedBanner] = useState(null);
  const [bannerCrop, setBannerCrop] = useState({ x: 0, y: 0 });
  const [bannerZoom, setBannerZoom] = useState(1);
  const [bannerChanged, setBannerChanged] = useState(false);

  const [uploading, setUploading] = useState(false);

  const { isMobile } = useContext(DeviceContext);
  const navigate = useNavigate();
  const { isLoggedIn, user } = useContext(AuthContext);

  useEffect(() => {
    if (!isLoggedIn) navigate("/signin");
  }, [isLoggedIn, navigate]);

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconInputName(file.name);
    setIconUrl(URL.createObjectURL(file));
    setIconChanged(true);
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerInputName(file.name);
    setBannerUrl(URL.createObjectURL(file));
    setBannerChanged(true);
  };

  const handleDeleteIcon = () => {
    setIconUrl(null);
    setCroppedIcon(null);
    setIconInputName("");
    setIconChanged(false);
  };

  const handleDeleteBanner = () => {
    setBannerUrl(null);
    setCroppedBanner(null);
    setBannerInputName("");
    setBannerChanged(false);
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      let iconUploadUrl = null;
      let bannerUploadUrl = null;

      if (iconChanged && croppedIcon) {
        const blob = await fetch(croppedIcon).then((r) => r.blob());
        const file = new File([blob], iconInputName, { type: blob.type });
        iconUploadUrl = await uploadFile(file, file.name, user.token);
      }
      if (bannerChanged && croppedBanner) {
        const blob = await fetch(croppedBanner).then((r) => r.blob());
        const file = new File([blob], bannerInputName, { type: blob.type });
        bannerUploadUrl = await uploadFile(file, file.name, user.token);
      }

      const response = await fetch(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/community/createCommunity`,
        {
          method: "POST",
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userName: user.username,
            name: communityName,
            description: communityDescription,
            rules: communityRules,
            icon: iconUploadUrl,
            banner: bannerUploadUrl,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        navigate(`/community/${data.communityName}`);
      } else {
        alert("Failed to create community");
      }
    } catch (error) {
      console.error("Error creating community:", error);
      alert("Failed to create community");
    } finally {
      setUploading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={2.5}>
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: DL.textSecondary, mb: 1 }}>
                Community Name
              </Typography>
              <CommunityNameInput setCommunityName={setCommunityName} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: DL.textSecondary, mb: 1 }}>
                Description
              </Typography>
              <TextField
                label="Tell people what your community is about"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={communityDescription}
                onChange={(e) => setCommunityDescription(e.target.value)}
                sx={inputSx}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: DL.textSecondary, mb: 1 }}>
                Community Rules
              </Typography>
              <TextField
                label="Set the rules for your community"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={communityRules}
                onChange={(e) => setCommunityRules(e.target.value)}
                sx={inputSx}
              />
            </Box>
          </Stack>
        );

      case 1:
        return (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: DL.green, mb: 0.5, textAlign: "center" }}>
              Community Icon
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: DL.textSecondary, mb: 2, textAlign: "center" }}>
              Upload a square image for your community icon
            </Typography>
            <input type="file" accept="image/*" onChange={handleIconChange} style={{ display: "none" }} id="icon-upload" />
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <label htmlFor="icon-upload">
                <Button component="span" sx={{ background: "linear-gradient(to right, #10b981, #059669)", color: "#fff", borderRadius: "8px", px: 2.5, py: 1, fontWeight: 700, fontSize: "0.85rem", textTransform: "none", boxShadow: "0px 2px 8px rgba(98,244,129,0.3)", "&:hover": { filter: "brightness(110%)" } }}>
                  Choose Image
                </Button>
              </label>
            </Box>
            {iconInputName && (
              <Typography sx={{ fontSize: "0.75rem", color: DL.textMuted, textAlign: "center", mb: 1.5 }}>
                {iconInputName}
              </Typography>
            )}
            {iconUrl ? (
              <Box sx={{ position: "relative", borderRadius: "12px", overflow: "hidden", border: `1px solid ${DL.borderGreen}` }}>
                <ImageCropper
                  imageSrc={iconUrl}
                  onCropComplete={(img) => { setCroppedIcon(img); setIconChanged(true); }}
                  cropShape="round"
                  aspect={1}
                  crop={iconCrop}
                  zoom={iconZoom}
                  onCropChange={setIconCrop}
                  onZoomChange={setIconZoom}
                />
                <IconButton onClick={handleDeleteIcon} size="small" sx={{ position: "absolute", top: 8, right: 8, backgroundColor: "rgba(255,100,103,0.85)", "&:hover": { backgroundColor: DL.red }, color: "#fff" }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ border: `1px dashed ${DL.borderGreen}`, borderRadius: "12px", height: isMobile ? "12rem" : "20rem", display: "flex", alignItems: "center", justifyContent: "center", color: DL.textMuted, fontSize: "0.875rem" }}>
                Select an image to begin
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: DL.green, mb: 0.5, textAlign: "center" }}>
              Community Banner
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: DL.textSecondary, mb: 2, textAlign: "center" }}>
              Upload a wide image for your community banner
            </Typography>
            <input type="file" accept="image/*" onChange={handleBannerChange} style={{ display: "none" }} id="banner-upload" />
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <label htmlFor="banner-upload">
                <Button component="span" sx={{ background: "linear-gradient(to right, #10b981, #059669)", color: "#fff", borderRadius: "8px", px: 2.5, py: 1, fontWeight: 700, fontSize: "0.85rem", textTransform: "none", boxShadow: "0px 2px 8px rgba(98,244,129,0.3)", "&:hover": { filter: "brightness(110%)" } }}>
                  Choose Image
                </Button>
              </label>
            </Box>
            {bannerInputName && (
              <Typography sx={{ fontSize: "0.75rem", color: DL.textMuted, textAlign: "center", mb: 1.5 }}>
                {bannerInputName}
              </Typography>
            )}
            {bannerUrl ? (
              <Box sx={{ position: "relative", borderRadius: "12px", overflow: "hidden", border: `1px solid ${DL.borderGreen}` }}>
                <ImageCropper
                  imageSrc={bannerUrl}
                  onCropComplete={(img) => { setCroppedBanner(img); setBannerChanged(true); }}
                  cropShape="rect"
                  aspect={3 / 1}
                  crop={bannerCrop}
                  zoom={bannerZoom}
                  onCropChange={setBannerCrop}
                  onZoomChange={setBannerZoom}
                />
                <IconButton onClick={handleDeleteBanner} size="small" sx={{ position: "absolute", top: 8, right: 8, backgroundColor: "rgba(255,100,103,0.85)", "&:hover": { backgroundColor: DL.red }, color: "#fff" }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ border: `1px dashed ${DL.borderGreen}`, borderRadius: "12px", height: isMobile ? "12rem" : "20rem", display: "flex", alignItems: "center", justifyContent: "center", color: DL.textMuted, fontSize: "0.875rem" }}>
                Select an image to begin
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: DL.green, mb: 2, textAlign: "center" }}>
              Preview
            </Typography>
            {/* Banner */}
            <Box
              sx={{
                width: "100%",
                height: 120,
                borderRadius: "8px",
                overflow: "hidden",
                background: croppedBanner
                  ? `url(${croppedBanner}) center/cover no-repeat`
                  : `linear-gradient(135deg, rgba(16,56,32,0.5) 0%, rgba(6,30,20,0.5) 100%)`,
                border: `1px solid ${DL.border}`,
                position: "relative",
              }}>
              {croppedBanner && (
                <IconButton onClick={handleDeleteBanner} size="small" sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, backgroundColor: "rgba(255,100,103,0.85)", "&:hover": { backgroundColor: DL.red }, color: "#fff" }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
            {/* Icon + name */}
            <Box sx={{ px: 2, mt: "-28px" }}>
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    src={croppedIcon || undefined}
                    sx={{ width: 64, height: 64, border: `3px solid ${DL.bgMedium}`, boxShadow: `0 0 0 1px ${DL.borderGreen}`, bgcolor: DL.bgLight, fontSize: "1.5rem", fontWeight: 700 }}>
                    {communityName?.charAt(0).toUpperCase()}
                  </Avatar>
                  {croppedIcon && (
                    <IconButton onClick={handleDeleteIcon} size="small" sx={{ position: "absolute", top: -4, right: -4, backgroundColor: "rgba(255,100,103,0.85)", "&:hover": { backgroundColor: DL.red }, color: "#fff", width: 18, height: 18 }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  )}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: DL.textPrimary, mb: 0.5 }}>
                  {communityName || "Community Name"}
                </Typography>
              </Box>
            </Box>
            {/* Summary */}
            {communityDescription && (
              <Box sx={{ mt: 2, p: 1.5, backgroundColor: DL.bgLight, borderRadius: "8px", border: `1px solid ${DL.border}` }}>
                <Typography sx={{ fontSize: "0.8rem", color: DL.textMuted, mb: 0.5, fontWeight: 600 }}>Description</Typography>
                <Typography sx={{ fontSize: "0.875rem", color: DL.textSecondary }}>{communityDescription}</Typography>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const isLastStep = activeStep === steps.length - 1;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.06) 0%, ${DL.bgDark} 60%)`,
        pt: "64px",
      }}>
      <ResponsiveTopBar isMobile={isMobile} onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)} />
      <MobileNavDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      <Box sx={{ maxWidth: 560, mx: "auto", px: 2, py: 4 }}>
        <Box sx={{ backgroundColor: DL.bgMedium, border: `1px solid ${DL.border}`, borderRadius: "12px", overflow: "hidden" }}>
          {/* Header */}
          <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${DL.divider}` }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: DL.textPrimary, lineHeight: 1.2 }}>
              Create a Community
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: DL.textMuted, mt: 0.5 }}>
              Build a space for traders and investors to connect
            </Typography>
          </Box>

          {/* Stepper */}
          <Box sx={{ px: 3, pt: 2.5 }}>
            {isMobile ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: DL.textSecondary }}>
                  Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={((activeStep + 1) / steps.length) * 100}
                  sx={{
                    mt: 0.8,
                    height: 6,
                    borderRadius: 999,
                    backgroundColor: `${DL.green}22`,
                    "& .MuiLinearProgress-bar": { backgroundColor: DL.green },
                  }}
                />
              </Box>
            ) : (
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  mb: 3,
                  "& .MuiStepLabel-label": { color: DL.textMuted, fontSize: "0.8rem" },
                  "& .MuiStepLabel-label.Mui-active": { color: DL.green },
                  "& .MuiStepLabel-label.Mui-completed": { color: DL.textSecondary },
                  "& .MuiStepIcon-root": { color: DL.bgLight },
                  "& .MuiStepIcon-root.Mui-active": { color: DL.green },
                  "& .MuiStepIcon-root.Mui-completed": { color: DL.greenEmerald },
                  "& .MuiStepIcon-text": { fill: DL.textPrimary, fontSize: "0.7rem", fontWeight: 700 },
                  "& .MuiStepConnector-line": { borderColor: DL.border },
                  "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": { borderColor: DL.greenEmerald },
                }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
          </Box>

          {/* Step content */}
          <Box sx={{ px: 3, pb: 1 }}>
            {getStepContent(activeStep)}
          </Box>

          {/* Navigation */}
          <Box sx={{ px: 3, py: 3 }}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Button
                onClick={() => activeStep === 0 ? navigate(-1) : setActiveStep((p) => p - 1)}
                sx={secondaryBtnSx}>
                {activeStep === 0 ? "Cancel" : "Back"}
              </Button>
              {isLastStep ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={uploading || !communityName}
                  sx={{ ...primaryBtnSx, flex: 1 }}>
                  {uploading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Create Community"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => setActiveStep((p) => p + 1)}
                  disabled={activeStep === 0 && !communityName}
                  sx={{ ...primaryBtnSx, flex: 1 }}>
                  Continue
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default CreateCommunity;
